import { forwardRef, FunctionComponent, useCallback, useEffect, useRef, useState } from "react";

import { CaseStudy } from "../schema/caseStudies";
import { ContentStatus } from "../schema/knowledgeDb";

import { ContentSection } from "./ContentSection";

interface CaseStudyContentAreaProps {
	caseStudies: CaseStudy[];
	activeCaseStudyId: number | null;
	onCaseStudyChange: (updated: CaseStudy) => void;
	onCaseStudyDelete: (caseStudyId: number) => void;
	onCaseStudyApprove: (caseStudyId: number) => void;
	onCaseStudyUnapprove: (caseStudyId: number) => void;
}

export const CaseStudyContentArea: FunctionComponent<CaseStudyContentAreaProps> = ({
	caseStudies,
	activeCaseStudyId,
	onCaseStudyChange,
	onCaseStudyDelete,
	onCaseStudyApprove,
	onCaseStudyUnapprove,
}) => {
	const caseStudyRefs = useRef<Record<number, HTMLDivElement | null>>({});

	useEffect(() => {
		if (activeCaseStudyId && caseStudyRefs.current[activeCaseStudyId]) {
			caseStudyRefs.current[activeCaseStudyId]?.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		}
	}, [activeCaseStudyId]);

	const updateCaseStudy = useCallback(
		(caseStudyId: number, partial: Partial<CaseStudy>) => {
			const cs = caseStudies.find((c) => c.id === caseStudyId);

			if (cs) {
				onCaseStudyChange({ ...cs, ...partial });
			}
		},
		[caseStudies, onCaseStudyChange]
	);

	const handleToggleEnrichment = useCallback(
		(caseStudyId: number) => {
			const cs = caseStudies.find((c) => c.id === caseStudyId);

			if (cs) {
				const isCurrentlyEnriched = !!cs.enrichmentSource;

				onCaseStudyChange({
					...cs,
					enrichmentSource: isCurrentlyEnriched ? "" : "linkedin",
					description: isCurrentlyEnriched
						? cs.description
						: cs.description || "Auto-enriched company description.",
				});
			}
		},
		[caseStudies, onCaseStudyChange]
	);

	if (caseStudies.length === 0) {
		return (
			<div className="flex-1 flex items-center justify-center text-sm text-gray-500 min-h-[400px]">
				No case studies yet. Click &quot;+ Add Case Study&quot; to get started.
			</div>
		);
	}

	return (
		<div className="flex-1 overflow-y-auto">
			{caseStudies.map((cs) => (
				<CaseStudyCard
					key={cs.id}
					ref={(el) => {
						caseStudyRefs.current[cs.id] = el;
					}}
					caseStudy={cs}
					onApprove={() => onCaseStudyApprove(cs.id)}
					onDelete={() => onCaseStudyDelete(cs.id)}
					onToggleEnrichment={() => handleToggleEnrichment(cs.id)}
					onUnapprove={() => onCaseStudyUnapprove(cs.id)}
					onUpdate={(partial) => updateCaseStudy(cs.id, partial)}
				/>
			))}
		</div>
	);
};

/* ───────── Individual case study card ───────── */

interface CaseStudyCardProps {
	caseStudy: CaseStudy;
	onUpdate: (partial: Partial<CaseStudy>) => void;
	onDelete: () => void;
	onToggleEnrichment: () => void;
	onApprove: () => void;
	onUnapprove: () => void;
}

const CaseStudyCard = forwardRef<HTMLDivElement, CaseStudyCardProps>(
	({ caseStudy: cs, onUpdate, onDelete, onToggleEnrichment, onApprove, onUnapprove }, ref) => {
		return (
			<div ref={ref} className="mb-8 px-6 bg-white">
				{/* Sticky header */}
				<div className="sticky top-0 z-10 bg-white pb-3 mb-4 border-b border-gray-200 py-4">
					<div className="flex items-center justify-between">
						<div className="flex items-center gap-3">
							<h2 className="text-ssmd font-bold text-gray-900">{cs.clientName}</h2>
							<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
								{cs.matchedSegment}
							</span>
							{cs.status === ContentStatus.AI_DRAFTED && (
								<span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-brand-white border border-blue-action/40 text-blue-action">
									✨ AI
								</span>
							)}
							{cs.status === ContentStatus.DRAFT && (
								<span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-status-warning-light border border-status-warning/40 text-status-warning">
									✎ Draft
								</span>
							)}
							{cs.status === ContentStatus.HUMAN_APPROVED && (
								<span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-status-success-light border border-status-success/40 text-status-success">
									✓ Approved
								</span>
							)}
						</div>
						<div className="flex items-center gap-2">
							{(cs.status === ContentStatus.AI_DRAFTED || cs.status === ContentStatus.DRAFT) && (
								<button
									className="text-xs font-medium cursor-pointer text-status-warning hover:text-status-warning/80 px-2 py-1 rounded transition-colors"
									type="button"
									onClick={onApprove}
								>
									✓ Approve
								</button>
							)}
							{cs.status === ContentStatus.HUMAN_APPROVED && (
								<button
									className="text-xs font-medium cursor-pointer text-status-warning hover:text-status-warning/80 px-2 py-1 rounded transition-colors"
									type="button"
									onClick={onUnapprove}
								>
									✗ Unapprove
								</button>
							)}
							<button
								className="text-xs font-medium cursor-pointer text-red-500 hover:text-red-500/80 px-2 py-1 rounded transition-colors"
								type="button"
								onClick={onDelete}
							>
								Delete
							</button>
						</div>
					</div>
					{cs.projectName && <p className="text-sm text-gray-500 mt-1">{cs.projectName}</p>}
				</div>

				{/* Enrichment data box */}
				<div className="py-4">
					<div className="rounded-xl border border-gray-300 bg-brand-white mb-4 p-4">
						<div className="flex items-center justify-between mb-2">
							<span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
								{cs.enrichmentSource ? `✓ Enriched (${cs.enrichmentSource})` : "Not enriched"}
							</span>
							<button
								className="text-xs font-medium text-brand-light hover:text-blue-action transition-colors cursor-pointer"
								type="button"
								onClick={onToggleEnrichment}
							>
								{cs.enrichmentSource ? "Disable auto-enrichment" : "Trigger auto-enrichment"}
							</button>
						</div>
						{cs.description && <p className="text-sm text-gray-700 mb-2">{cs.description}</p>}
						<div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
							{cs.industry.length > 0 && (
								<div className="flex items-center gap-1.5">
									{cs.industry.map((tag) => (
										<span
											key={tag}
											className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 text-[11px]"
										>
											{tag}
										</span>
									))}
								</div>
							)}
							{cs.employeeCount > 0 && <span>👥 {cs.employeeCount} employees</span>}
							{cs.website && (
								<a
									className="text-brand-light hover:text-blue-action underline"
									href={cs.website}
									rel="noopener noreferrer"
									target="_blank"
								>
									{cs.website}
								</a>
							)}
						</div>
					</div>

					{/* Project Name */}
					<ContentSection
						collapsible
						contentStatus={cs.status}
						editValue={cs.projectName}
						title="Project Name"
						onApprove={onApprove}
						onSave={(value) => onUpdate({ projectName: value })}
						onUnapprove={onUnapprove}
					>
						{cs.projectName || (
							<span className="text-gray-400 italic">No project name specified.</span>
						)}
					</ContentSection>

					{/* Goals / Challenges */}
					<ContentSection
						collapsible
						contentStatus={cs.status}
						editValue={cs.goalsChallenge}
						title="Goals / Challenges"
						onApprove={onApprove}
						onSave={(value) => onUpdate({ goalsChallenge: value })}
						onUnapprove={onUnapprove}
					>
						{cs.goalsChallenge || (
							<span className="text-gray-400 italic">No goals documented.</span>
						)}
					</ContentSection>

					{/* Solution / What We Did */}
					<ContentSection
						collapsible
						contentStatus={cs.status}
						editValue={cs.solution}
						title="Solution / What We Did"
						onApprove={onApprove}
						onSave={(value) => onUpdate({ solution: value })}
						onUnapprove={onUnapprove}
					>
						{cs.solution || <span className="text-gray-400 italic">No solution documented.</span>}
					</ContentSection>

					{/* Outcome / Impact */}
					<ContentSection
						collapsible
						contentStatus={cs.status}
						editValue={cs.outcome}
						title="Outcome / Impact"
						onApprove={onApprove}
						onSave={(value) => onUpdate({ outcome: value })}
						onUnapprove={onUnapprove}
					>
						{cs.outcome || <span className="text-gray-400 italic">No outcome documented.</span>}
					</ContentSection>

					{/* Quote section */}
					<QuoteSection caseStudy={cs} onEdit={(partial) => onUpdate(partial)} />
				</div>
			</div>
		);
	}
);

CaseStudyCard.displayName = "CaseStudyCard";

/* ───────── Quote section with indigo left border ───────── */

interface QuoteSectionProps {
	caseStudy: CaseStudy;
	onEdit: (partial: Partial<CaseStudy>) => void;
}

const QuoteSection: FunctionComponent<QuoteSectionProps> = ({ caseStudy, onEdit }) => {
	const [isEditing, setIsEditing] = useState(false);
	const [editQuote, setEditQuote] = useState(caseStudy.quote);
	const [editAttribution, setEditAttribution] = useState(caseStudy.quoteAttribution);

	const handleEdit = () => {
		setEditQuote(caseStudy.quote);
		setEditAttribution(caseStudy.quoteAttribution);
		setIsEditing(true);
	};

	const handleSave = () => {
		onEdit({ quote: editQuote, quoteAttribution: editAttribution });
		setIsEditing(false);
	};

	const handleCancel = () => {
		setEditQuote(caseStudy.quote);
		setEditAttribution(caseStudy.quoteAttribution);
		setIsEditing(false);
	};

	const hasQuote = caseStudy.quote || caseStudy.quoteAttribution;

	return (
		<div className="rounded-xl border border-gray-300 bg-white mb-4">
			<div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
				<h3 className="text-sm font-semibold text-gray-900">Quote</h3>
				{!isEditing && (
					<button
						className="text-xs font-medium cursor-pointer text-text-meta hover:text-blue-action px-2 py-1 rounded transition-colors"
						type="button"
						onClick={handleEdit}
					>
						Edit
					</button>
				)}
			</div>

			<div className="px-4 py-3">
				{isEditing ? (
					<div className="space-y-3">
						<div>
							<label className="block text-xs font-medium text-gray-600 mb-1">Quote text</label>
							<textarea
								className="w-full min-h-[80px] px-3 py-2 text-sm border border-gray-300 rounded-lg resize-y focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
								value={editQuote}
								onChange={(e) => setEditQuote(e.target.value)}
							/>
						</div>
						<div>
							<label className="block text-xs font-medium text-gray-600 mb-1">Attribution</label>
							<input
								className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-light/30 focus:border-brand-light"
								placeholder="e.g. John Doe, VP of Engineering, Acme Corp"
								type="text"
								value={editAttribution}
								onChange={(e) => setEditAttribution(e.target.value)}
							/>
						</div>
						<div className="flex justify-end gap-2">
							<button
								className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
								type="button"
								onClick={handleCancel}
							>
								Cancel
							</button>
							<button
								className="px-3 py-1.5 text-xs font-medium text-white bg-brand-light rounded-lg hover:bg-blue-action transition-colors"
								type="button"
								onClick={handleSave}
							>
								Save
							</button>
						</div>
					</div>
				) : hasQuote ? (
					<div className="border-l-4 border-indigo-500 pl-4 py-2">
						<p className="text-sm text-gray-700 italic leading-relaxed">
							&ldquo;{caseStudy.quote}&rdquo;
						</p>
						{caseStudy.quoteAttribution && (
							<p className="text-xs text-gray-500 mt-2">— {caseStudy.quoteAttribution}</p>
						)}
					</div>
				) : (
					<span className="text-gray-400 italic text-sm">No quote added.</span>
				)}
			</div>
		</div>
	);
};
